import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import SEO from "@/components/SEO";
import CompanyLogoMarquee from "@/components/CompanyLogoMarquee";
import { useState, useEffect, useCallback } from "react";
import { trackEvent } from "@/utils/posthog";
import {
  ArrowRight,
  X,
  Bot,
  Gauge,
  Lock,
  CheckCircle2,
  Clock,
  Users,
  Calendar,
  Sparkles,
} from "lucide-react";

type WaitlistProgram = "ai-agents" | "top-gear";

// Waitlist capture form (Kit / ConvertKit). Both programs share this form today;
// program attribution is tracked via PostHog. To route Top Gear to a dedicated
// form later, swap in its own data-uid + src and inject conditionally on program.
const KIT_FORM_UID = "b00140dfe6";
const KIT_FORM_SRC = "https://techleaders.kit.com/b00140dfe6/index.js";

const MODAL_COPY: Record<WaitlistProgram, { title: string; subtitle: string }> = {
  "ai-agents": {
    title: "Join the AI Agents Program waitlist",
    subtitle:
      "The current cohort is full. Drop your email and you'll be first to get an invite the moment a seat opens.",
  },
  "top-gear": {
    title: "Request a Top Gear invitation",
    subtitle:
      "Top Gear is invite-only. Tell us where to reach you and we'll review your fit for the next intake.",
  },
};

const AI_AGENTS_BENEFITS = [
  "Build practical, no-code AI agents that work with the tools you already use",
  "Hands-on cohort with live builds, so you ship something useful, not just theory",
  "The AI-1st™ Playbook: literacy, leverage, and leadership without writing code",
  "Weekly AI Mastermind with member hot seats and a 300+ community of AI operators",
  "First access when a seat opens in the next cohort",
];

const TOP_GEAR_BENEFITS = [
  "For operators already shipping with AI who want the next gear",
  "Small, invite-only room: signal over noise, no beginners",
  "Advanced agent architectures, workflows, and behind-the-scenes builds",
  "Direct access to Todd, Stephen, and a vetted peer group",
  "Reviewed by application, with seats deliberately limited",
];

const STEPS = [
  {
    icon: Users,
    title: "Raise your hand",
    body: "Join the waitlist or request an invite. Takes 20 seconds, with no payment to get in line.",
  },
  {
    icon: Calendar,
    title: "We reach out",
    body: "When the AI Agents cohort reopens or a Top Gear seat opens, waitlist members hear first.",
  },
  {
    icon: Sparkles,
    title: "You're in",
    body: "Onboard, meet the room, and start shipping with AI alongside people doing the same.",
  },
];

const FAQS = [
  {
    q: "When does the AI Agents Program reopen?",
    a: "The current cohort is full. When we reopen, waitlist members get the invite before anyone else, including any early-bird pricing.",
  },
  {
    q: "How does the Top Gear invite work?",
    a: "Top Gear is invite-only and reviewed by application. Request an invite and we'll look at where you are with AI. If it's a fit, we'll reach out when a seat opens. Seats are deliberately limited.",
  },
  {
    q: "Does joining the waitlist cost anything?",
    a: "No. Adding your email or requesting an invite is free and commits you to nothing. It only puts you first in line when we reopen.",
  },
  {
    q: "Which one is right for me?",
    a: "Choose the AI Agents Program if you want the flagship, hands-on path to building AI agents, no coding required. Choose Top Gear if you're already shipping with AI and want a small, advanced room to go faster.",
  },
  {
    q: "Am I guaranteed a spot?",
    a: "The waitlist guarantees you're first to be invited, not an automatic seat. Cohorts are capped on purpose so the room stays high-signal, so joining early is how you get in.",
  },
];

const AIWaitlist = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeProgram, setActiveProgram] = useState<WaitlistProgram>("ai-agents");

  // Load the Kit form once on mount into the always-rendered container.
  useEffect(() => {
    const formContainer = document.getElementById("waitlist-modal-form");
    if (formContainer && !formContainer.querySelector("script")) {
      const script = document.createElement("script");
      script.async = true;
      script.setAttribute("data-uid", KIT_FORM_UID);
      script.src = KIT_FORM_SRC;
      formContainer.appendChild(script);
    }
  }, []);

  // Load the testimonial.to embed script (same carousel used on /ai-program).
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://testimonial.to/js/iframeResizer.min.js";
    script.async = true;
    script.onload = () => {
      const w = window as unknown as {
        iFrameResize?: (opts: Record<string, unknown>, selector: string) => void;
      };
      if (w.iFrameResize) {
        w.iFrameResize(
          { log: false, checkOrigin: false },
          "#testimonialto-ai-waitlist-948cd5e8"
        );
      }
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // Close on escape + lock body scroll while open.
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsModalOpen(false);
    };
    if (isModalOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isModalOpen]);

  const openModal = useCallback((program: WaitlistProgram, location: string) => {
    setActiveProgram(program);
    setIsModalOpen(true);
    trackEvent("Waitlist Modal Opened", { program, location });
  }, []);

  const modalCopy = MODAL_COPY[activeProgram];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="Join the Waitlist | Technical Leaders"
        description="We're closed, on purpose. Get first access to the AI Agents Program when it reopens, or request an invite to Top Gear, our invite-only advanced AI group."
        keywords={[
          "AI Agents Program",
          "Top Gear",
          "AI waitlist",
          "technical leaders",
          "invite only AI group",
          "AI cohort",
        ]}
      />

      <Navigation />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-background to-secondary/20 pt-28 pb-16 sm:pt-32 sm:pb-20">
        <div className="absolute inset-0 z-0">
          <img
            src="/ai-in-ar.png"
            alt="AI background"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/60 to-background" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-longhand-accent/30 bg-longhand-accent/10 px-4 py-1.5 text-sm font-semibold text-longhand-accent mb-8">
            <Lock className="h-3.5 w-3.5" />
            Enrollment is currently closed
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            <span className="text-longhand-accent">We're Closed Right Now</span>
          </h1>

          <p className="text-xl sm:text-2xl font-semibold text-foreground mb-4">
            We cap every cohort so we can help everyone closely.
          </p>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground mb-10">
            There are two ways to work with us. Add your name to the waitlist and we'll let you
            know when the next spot opens.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              size="lg"
              className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 text-white text-lg px-8 py-6"
              onClick={() => openModal("ai-agents", "hero")}
            >
              Join the AI Agents Waitlist
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-longhand-accent/40 text-longhand-accent hover:bg-longhand-accent/10 text-lg px-8 py-6"
              onClick={() => openModal("top-gear", "hero")}
            >
              Request a Top Gear Invite
              <Gauge className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Company logos */}
      <CompanyLogoMarquee
        footnote="Hundreds of leaders, operators, and builders from agencies, startups, non-profits, and Fortune 500 companies are already inside."
      />

      {/* Two program options */}
      <section className="pt-16 pb-8">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">
              Two ways to work with us
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Same team, same playbook. One flagship cohort, one advanced inner circle.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
            {/* AI Agents Program */}
            <Card className="flex flex-col p-8 border-sky-500/30 hover:border-sky-500/60 transition-colors">
              <div className="inline-flex self-start items-center gap-2 rounded-full bg-sky-500/10 text-sky-400 px-3 py-1 text-xs font-semibold mb-5">
                <Clock className="h-3.5 w-3.5" />
                COHORT FULL · REOPENING SOON
              </div>

              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-blue-600">
                  <Bot className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-foreground">AI Agents Program</h3>
              </div>

              <p className="text-muted-foreground mb-6">
                Our flagship, hands-on cohort. Build practical AI agents that do real work,
                no coding required.
              </p>

              <div className="space-y-3 mb-8">
                {AI_AGENTS_BENEFITS.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-sky-400 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="mt-auto">
                <Button
                  size="lg"
                  className="w-full bg-sky-500 hover:bg-sky-600 text-white text-base px-6 py-6"
                  onClick={() => openModal("ai-agents", "program_card")}
                >
                  Join the Waitlist
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <p className="mt-3 text-center text-xs text-muted-foreground">
                  Free to join · First in line when we reopen
                </p>
              </div>
            </Card>

            {/* Top Gear */}
            <Card className="relative flex flex-col p-8 border-longhand-accent/40 hover:border-longhand-accent/70 transition-colors ring-1 ring-longhand-accent/20">
              <div className="absolute -top-3 right-6 rounded-full bg-longhand-accent px-3 py-1 text-xs font-bold text-white shadow-lg">
                INVITE-ONLY
              </div>

              <div className="inline-flex self-start items-center gap-2 rounded-full bg-longhand-accent/10 text-longhand-accent px-3 py-1 text-xs font-semibold mb-5">
                <Lock className="h-3.5 w-3.5" />
                ADVANCED · BY APPLICATION
              </div>

              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-longhand-accent to-longhand-accent-hover">
                  <Gauge className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-foreground">Top Gear</h3>
              </div>

              <p className="text-muted-foreground mb-6">
                A small, invite-only room for operators already shipping with AI who want to
                move faster than everyone else.
              </p>

              <div className="space-y-3 mb-8">
                {TOP_GEAR_BENEFITS.map((benefit) => (
                  <div key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-longhand-accent flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="mt-auto">
                <Button
                  size="lg"
                  className="w-full bg-longhand-accent hover:bg-longhand-accent-hover text-white font-semibold text-base px-6 py-6"
                  onClick={() => openModal("top-gear", "program_card")}
                >
                  Request an Invitation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <p className="mt-3 text-center text-xs text-muted-foreground">
                  Reviewed personally · Limited seats
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* What happens next */}
      <section className="py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
              What happens after you join
            </h2>
            <p className="text-muted-foreground">No pressure, no payment, just your spot in line.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {STEPS.map((step, i) => (
              <div key={step.title} className="text-center px-4">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary">
                  <step.icon className="h-6 w-6 text-longhand-accent" />
                </div>
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                  Step {i + 1}
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials: same section as /ai-program */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Real Results
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-600">
                for AI-First Leaders
              </span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Don't just take our word for it. Here's what members in our program are saying.
            </p>
          </div>

          {/* Testimonial.to Embed */}
          <div>
            <iframe
              id="testimonialto-ai-waitlist-948cd5e8"
              src="https://embed-v2.testimonial.to/carousel/all/tech-leaders-mastermind-program?id=948cd5e8-1b47-43b4-8da1-a15b4bbdfb8e"
              frameBorder="0"
              scrolling="no"
              width="100%"
            ></iframe>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-foreground mb-10">
            Questions before you join
          </h2>
          <div className="space-y-4">
            {FAQS.map((faq) => (
              <Card key={faq.q} className="p-6">
                <h3 className="font-semibold text-foreground mb-2">{faq.q}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            The doors reopen soon. Be first through them.
          </h2>
          <p className="text-lg text-muted-foreground mb-10">
            Join the AI Agents waitlist, or request your Top Gear invite. Both take 20 seconds
            and cost nothing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              size="lg"
              className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 text-white text-lg px-8 py-6"
              onClick={() => openModal("ai-agents", "final_cta")}
            >
              Join the AI Agents Waitlist
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-longhand-accent/40 text-longhand-accent hover:bg-longhand-accent/10 text-lg px-8 py-6"
              onClick={() => openModal("top-gear", "final_cta")}
            >
              Request a Top Gear Invite
              <Gauge className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Waitlist / invite modal: always rendered so the Kit script injects reliably */}
      <div
        className={`fixed inset-0 z-50 ${isModalOpen ? "visible" : "invisible"}`}
        aria-hidden={!isModalOpen}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/80 transition-opacity duration-200 ${
            isModalOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setIsModalOpen(false)}
        />

        {/* Modal content */}
        <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[calc(100%-2rem)] max-w-md">
          <div
            className={`w-full bg-background border rounded-lg shadow-lg p-6 transition-all duration-200 ${
              isModalOpen ? "opacity-100 scale-100" : "opacity-0 scale-95"
            }`}
          >
            <div className="flex items-start justify-between mb-1 gap-4">
              <h2 className="text-lg font-semibold text-foreground">{modalCopy.title}</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="rounded-sm opacity-70 hover:opacity-100 transition-opacity mt-1"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{modalCopy.subtitle}</p>
            {/* Shared Kit form. Program is attributed via the tracked open event above. */}
            <div id="waitlist-modal-form" className="min-h-[200px]" />
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AIWaitlist;
